import React, { JSX, useContext } from 'react';

import { StatsigExperimentContext } from '../../context/NavSearchContext';
import { getConversationalSearchUnavailableMessage } from './conversationalSearchMessaging';

/**
 * Sparkle/pink-purple gradient pulled directly from the design. These match the
 * `GLOW_PURPLE` / `GLOW_PINK` constants used by SearchWithAIToggle's glow.
 */
const SPARKLE_PURPLE = '#9952e0';
const SPARKLE_PINK = '#ff66e6';
const SPARKLE_GRADIENT_ID = 'conversational-search-cta-sparkle';

// Soft pink-purple wash for the 2px ring around the white pill (the design's border).
const GRADIENT_WASH =
    'radial-gradient(51.54% 601.84% at 3.7% 100%, rgba(153, 82, 224, 0.3) 0%, rgba(255, 102, 230, 0.3) 100%)';

// Bouncing border bloom: a blurred pink-purple ellipse sitting just behind the ring that slides
// left↔right, so the border appears to thicken and glow at the moving spot. The blob is animated
// with `transform: translateX` (compositor-driven, so the blur is rasterized once and just moved)
// rather than `background-position` (which would re-rasterize the blur every frame and stutter).
// Defined in a scoped <style> tag (Tailwind's JIT can't scan this, and keyframes can't be inline).
const BLOOM_PURPLE = 'rgba(153, 82, 224, 0.9)';
const BLOOM_PINK = 'rgba(255, 102, 230, 0.6)';
const BLOOM_DURATION_S = 1.5;
const BLOOM_CLASS = 'conv-search-cta-border-bloom';

const BLOOM_STYLES = `
    .${BLOOM_CLASS} {
        background-image: radial-gradient(closest-side, ${BLOOM_PURPLE} 0%, ${BLOOM_PINK} 55%, transparent 100%);
        will-change: transform;
        animation: ${BLOOM_CLASS}-bounce ${BLOOM_DURATION_S}s linear infinite alternate;
    }
    @keyframes ${BLOOM_CLASS}-bounce {
        /* The blob is 45% wide and anchored at the left; these offsets carry its centre from the
           left rounded end to the right one (barely overshooting so the caps glow, no further). */
        from { transform: translateX(-55%); }
        to { transform: translateX(177%); }
    }
    @media (prefers-reduced-motion: reduce) {
        .${BLOOM_CLASS} {
            animation: none;
            opacity: 0;
        }
    }
`;

/**
 * Prominent-search CTA that navigates straight to conversational search. A white
 * pill wrapped in a pink-purple gradient ring with a soft blurred glow behind it,
 * matching the "Conversational Search" entry point in the design.
 *
 * When `disabledForContentType` is set, conversational search isn't available for the
 * current content type (same restriction as SearchWithAIToggle): the CTA renders greyed
 * out and non-interactive, dropping the gradient ring and animated bloom so it doesn't
 * draw attention to an unavailable feature.
 */
export default function ConversationalSearchButton({
    onClick,
    disabledForContentType = false,
}: {
    onClick: () => void;
    disabledForContentType?: boolean;
}): JSX.Element {
    const { showConversationalSearchAudio } = useContext(StatsigExperimentContext);

    if (disabledForContentType) {
        const disabledLabel = getConversationalSearchUnavailableMessage(showConversationalSearchAudio ?? false);
        return (
            <div className="relative m-2 hidden shrink-0 grow-0 md:block">
                <button
                    type="button"
                    aria-disabled
                    aria-label={disabledLabel}
                    title={disabledLabel}
                    className="relative flex h-[46px] cursor-not-allowed items-center justify-center gap-2 rounded-full border border-solid border-gray-500 bg-gray-200 px-4"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 27 26"
                        width={20}
                        height={20}
                        aria-hidden="true"
                    >
                        <path
                            fill="var(--color-gray-700)"
                            d="M23.25 18a.75.75 0 0 1 .75.75A2.25 2.25 0 0 0 26.25 21a.75.75 0 0 1 0 1.5A2.25 2.25 0 0 0 24 24.75a.75.75 0 0 1-1.5 0 2.25 2.25 0 0 0-2.25-2.25.75.75 0 0 1 0-1.5 2.25 2.25 0 0 0 2.25-2.25.75.75 0 0 1 .75-.75"
                        />
                        <path
                            fill="var(--color-gray-700)"
                            d="M9.75 3a.75.75 0 0 1 .75.75A8.25 8.25 0 0 0 18.75 12a.75.75 0 0 1 0 1.5 8.25 8.25 0 0 0-8.25 8.25.75.75 0 0 1-1.5 0A8.25 8.25 0 0 0 .75 13.5a.75.75 0 0 1 0-1.5A8.25 8.25 0 0 0 9 3.75.75.75 0 0 1 9.75 3"
                        />
                        <path
                            fill="var(--color-gray-700)"
                            d="M23.25 0a.75.75 0 0 1 .75.75A2.25 2.25 0 0 0 26.25 3a.75.75 0 0 1 0 1.5A2.25 2.25 0 0 0 24 6.75a.75.75 0 0 1-1.5 0 2.25 2.25 0 0 0-2.25-2.25.75.75 0 0 1 0-1.5A2.25 2.25 0 0 0 22.5.75a.75.75 0 0 1 .75-.75"
                        />
                    </svg>
                    <span className="font-inter whitespace-nowrap text-base font-semibold text-gray-700">
                        Conversational Search
                    </span>
                </button>
            </div>
        );
    }

    return (
        <div className="relative m-2 hidden shrink-0 grow-0 md:block">
            {/* Gradient ring (2px) around the white pill. `overflow-hidden` clips the travelling
                bloom to the pill outline so it glows the border (and rounded caps) without ever
                spilling out past the button. */}
            <div className="relative overflow-hidden rounded-full p-0.5" style={{ background: GRADIENT_WASH }}>
                <style>{BLOOM_STYLES}</style>
                {/* Tall, narrow ellipse that slides along the border, glowing it as it goes.
                    Sits above the ring gradient but below the white pill (which is relative). */}
                <div
                    aria-hidden="true"
                    className={`pointer-events-none absolute -top-2 -bottom-2 left-0 w-[45%] blur-[3px] ${BLOOM_CLASS}`}
                />
                <button
                    type="button"
                    onClick={onClick}
                    aria-label="Conversational Search"
                    className="relative flex h-[46px] cursor-pointer items-center justify-center gap-2 rounded-full border-none bg-white px-4"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 27 26"
                        width={20}
                        height={20}
                        aria-hidden="true"
                    >
                        <defs>
                            <linearGradient id={SPARKLE_GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor={SPARKLE_PURPLE} />
                                <stop offset="100%" stopColor={SPARKLE_PINK} />
                            </linearGradient>
                        </defs>
                        <path
                            fill={`url(#${SPARKLE_GRADIENT_ID})`}
                            d="M23.25 18a.75.75 0 0 1 .75.75A2.25 2.25 0 0 0 26.25 21a.75.75 0 0 1 0 1.5A2.25 2.25 0 0 0 24 24.75a.75.75 0 0 1-1.5 0 2.25 2.25 0 0 0-2.25-2.25.75.75 0 0 1 0-1.5 2.25 2.25 0 0 0 2.25-2.25.75.75 0 0 1 .75-.75"
                        />
                        <path
                            fill={`url(#${SPARKLE_GRADIENT_ID})`}
                            d="M9.75 3a.75.75 0 0 1 .75.75A8.25 8.25 0 0 0 18.75 12a.75.75 0 0 1 0 1.5 8.25 8.25 0 0 0-8.25 8.25.75.75 0 0 1-1.5 0A8.25 8.25 0 0 0 .75 13.5a.75.75 0 0 1 0-1.5A8.25 8.25 0 0 0 9 3.75.75.75 0 0 1 9.75 3"
                        />
                        <path
                            fill={`url(#${SPARKLE_GRADIENT_ID})`}
                            d="M23.25 0a.75.75 0 0 1 .75.75A2.25 2.25 0 0 0 26.25 3a.75.75 0 0 1 0 1.5A2.25 2.25 0 0 0 24 6.75a.75.75 0 0 1-1.5 0 2.25 2.25 0 0 0-2.25-2.25.75.75 0 0 1 0-1.5A2.25 2.25 0 0 0 22.5.75a.75.75 0 0 1 .75-.75"
                        />
                    </svg>
                    <span className="font-inter whitespace-nowrap text-base font-semibold text-gray-800">
                        Conversational Search
                    </span>
                </button>
            </div>
        </div>
    );
}
